import { Formik, Form, Field, ErrorMessage } from "formik"
import { useContext } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import * as Yup from "yup"

import { POST } from "modules/Core/api/REST"
import useAuth from "modules/Core/hooks/useAuth"
import { AuthContent, FormItem } from "pages/AuthPage/AuthPage.styled"
import { Row } from "styles/Global.styled"
import { Button } from "ui/Button/Button.styled"
import Checkbox from "ui/Checkbox/Checkbox"
import { StyledLink } from "ui/Link/Link.styled"
import { renderError } from "utils/helpers"
import { InputComponent } from "utils/helpers/components/FormikField"

import { AuthFormContext } from "../../context/AuthFormContext"
import Password from "../Password/Password"

interface SignOnForm {
    phone: string
    email: string
    password: string
    confirmPassword: string
    publicOffer: boolean
}

const initialValues = {
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    publicOffer: false,
}

const validationSchema = Yup.object({
    email: Yup.string()
        .email("Введите корректный адрес эл.почты")
        .required("Поле E-mail обязательно для заполнения!"),
    phone: Yup.string()
        .matches(/^((\+7|8)\d{10})$/g, "Введите корректный номер телефона")
        .required("Поле телефон обязательно для заполнения!"),
    password: Yup.string()
        .required("Введите пароль (минимум 8 символов)")
        .min(8, "Минимум 8 символов"),
    confirmPassword: Yup.string().oneOf(
        [Yup.ref("password"), null],
        "Введенные пароли не совпадают!"
    ),
    publicOffer: Yup.boolean().oneOf([true], "Примите согласие с условиями публичной оферты"),
})

const SignOnForm = () => {
    const authContext = useContext(AuthFormContext)
    const location = useLocation()
    const navigate = useNavigate()

    const isAdmin = location.pathname.includes("/admin")
    const { setAuth } = useAuth()

    const from = location.state?.from?.pathname || "/"

    const handleSubmit = async (values: SignOnForm) => {
        try {
            const response = await POST(`/api/accounts/create/${isAdmin ? "admin" : "client"}`, {
                ...values,
            })

            if (response.data) {
                const { data } = response.data
                const { token: accessToken, user } = data

                const userData = { user, role: user?.user_type, accessToken }
                localStorage.setItem("auth_user_credentials", JSON.stringify(userData))
                setAuth(userData)
                navigate(from, { replace: true })
            } else {
                authContext?.setErrorMessage(
                    "Непредвиденная ошибка при регистрации. Попробуйте позднее"
                )
            }
            //eslint-disable-next-line
        } catch (err: any) {
            if (!err?.response) {
                authContext?.setErrorMessage(
                    "Непредвиденная ошибка при регистрации. Попробуйте позднее"
                )
            } else if (
                err?.response?.data?.detail ===
                "The user with this email already exists in the system"
            ) {
                authContext?.setErrorMessage(
                    "Пользователь с таким адресом электронной почты уже существует в системе"
                )
            } else {
                authContext?.setErrorMessage("Ошибка при регистрации (1)")
            }
        }
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnBlur={false}
            onSubmit={handleSubmit}
        >
            {(props) => (
                <Form>
                    <AuthContent>
                        <FormItem>
                            <label htmlFor="">E-mail</label>
                            <Field name="email" component={InputComponent} />
                            <ErrorMessage name="email" render={renderError} />
                        </FormItem>

                        <FormItem>
                            <label htmlFor="">Телефон</label>
                            <Field name="phone" component={InputComponent} />
                            <ErrorMessage name="phone" render={renderError} />
                        </FormItem>

                        <FormItem>
                            <label htmlFor="">Пароль</label>
                            <Password>
                                <Field
                                    name="password"
                                    type={authContext?.showPassword ? "text" : "password"}
                                    component={InputComponent}
                                />
                            </Password>
                            <ErrorMessage name="password" render={renderError} />
                        </FormItem>

                        <FormItem>
                            <label htmlFor="">Повторите пароль</label>
                            <Password>
                                <Field
                                    name="confirmPassword"
                                    type={authContext?.showPassword ? "text" : "password"}
                                    component={InputComponent}
                                />
                            </Password>

                            <ErrorMessage name="confirmPassword" render={renderError} />
                        </FormItem>
                    </AuthContent>

                    <Row gap="0.5rem" style={{ marginTop: "1rem" }}>
                        <Checkbox
                            checked={props.values.publicOffer}
                            handleChange={() => {
                                props.setFieldValue("publicOffer", !props.values.publicOffer)
                            }}
                        />
                        <div>
                            Согласен с условиями{" "}
                            <StyledLink to="/terms">публичной оферты</StyledLink>
                        </div>
                    </Row>
                    <ErrorMessage name="publicOffer" render={renderError} />

                    <Button type="submit" style={{ marginTop: "1rem" }}>
                        Зарегистрироваться
                    </Button>
                </Form>
            )}
        </Formik>
    )
}

export default SignOnForm
